import { logInfo } from '../config_and_helpers/logger';

const LOG_CONTEXT = 'ProductDeleteGuard';

/** Same copy `DeleteButton` uses when a delete response has no readable message. */
export const PRODUCT_DELETE_ERROR_FALLBACK =
  'An unexpected error occurred. Please try again in a little while.';

const OPEN_PROCUREMENT_CODE = 'product_open_procurement';

/**
 * Trim a corporate demand or order reference. Null and blank refs are unnamed.
 *
 * @param {unknown} value
 * @returns {string}
 */
function refText(value) {
  if (value == null) return '';
  return String(value).trim();
}

/**
 * True when `detail` already names this ref as its own token.
 * `D-1` inside `D-10` does not count.
 *
 * @param {string} detail
 * @param {string} ref
 * @returns {boolean}
 */
function detailAlreadyNamesRef(detail, ref) {
  if (!detail || !ref) return false;
  const escaped = ref.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  return new RegExp(`(^|[^A-Za-z0-9])${escaped}([^A-Za-z0-9]|$)`).test(detail);
}

/**
 * @param {unknown} rows
 * @param {string} detail
 * @returns {string[]}
 */
function quoteNamesMissingFromDetail(rows, detail) {
  if (!Array.isArray(rows)) return [];
  const names = [];
  rows.forEach(row => {
    const ref = refText(row?.corporate_demand_ref);
    if (!ref || detailAlreadyNamesRef(detail, ref)) return;
    const status = refText(row?.status);
    names.push(status ? `quote ${ref} (${status})` : `quote ${ref}`);
  });
  return names;
}

/**
 * @param {unknown} rows
 * @param {string} detail
 * @returns {string[]}
 */
function orderNamesMissingFromDetail(rows, detail) {
  if (!Array.isArray(rows)) return [];
  const names = [];
  rows.forEach(row => {
    const ref = refText(row?.corporate_order_ref);
    if (!ref || detailAlreadyNamesRef(detail, ref)) return;
    names.push(`order ${ref}`);
  });
  return names;
}

/**
 * @param {string[]} names
 * @returns {string}
 */
function formatNameList(names) {
  if (names.length === 1) return names[0];
  if (names.length === 2) return `${names[0]} and ${names[1]}`;
  return `${names.slice(0, -1).join(', ')}, and ${names[names.length - 1]}`;
}

/**
 * @param {unknown} rows
 * @param {string} refKey
 * @returns {boolean}
 */
function hasProcurementRows(rows, refKey) {
  return (
    Array.isArray(rows) &&
    rows.some(
      row =>
        row &&
        typeof row === 'object' &&
        (row.id != null || refText(row[refKey]))
    )
  );
}

/**
 * 409 body from `DELETE /products/{id}/` while this catalog number is in open procurement.
 *
 * @param {Object} data
 * @returns {boolean}
 */
function isOpenProcurementDeleteBody(data) {
  if (!data || typeof data !== 'object' || Array.isArray(data)) return false;
  if (data.code === OPEN_PROCUREMENT_CODE) return true;
  return (
    hasProcurementRows(data.open_quotes, 'corporate_demand_ref') ||
    hasProcurementRows(data.open_orders, 'corporate_order_ref')
  );
}

/**
 * Readable strings from a DRF field-error object. Skips nested objects so a
 * quote or order array cannot become `[object Object]`.
 *
 * @param {Object} data
 * @returns {string[]}
 */
function fieldMessages(data) {
  const messages = [];
  Object.values(data).forEach(value => {
    if (typeof value === 'string' && value.trim()) {
      messages.push(value.trim());
      return;
    }
    if (!Array.isArray(value)) return;
    value.forEach(item => {
      if (typeof item === 'string' && item.trim()) {
        messages.push(item.trim());
      } else if (item != null && typeof item !== 'object') {
        messages.push(String(item));
      }
    });
  });
  return messages;
}

/**
 * User-facing message for a failed `DELETE /products/{id}/`.
 *
 * Open-procurement refusals start with `detail`. Quote and order refs that
 * are not already in that sentence are named so the user can close them.
 * Any other body prefers `detail`, then `error`, then field messages.
 *
 * @param {unknown} data - `error.response.data`, or undefined when the request failed first
 * @returns {string}
 */
export function formatProductDeleteError(data) {
  if (data == null || data === '') {
    logInfo(LOG_CONTEXT, 'product delete error has no body', {});
    return PRODUCT_DELETE_ERROR_FALLBACK;
  }

  if (typeof data === 'string') {
    const text = data.trim();
    logInfo(LOG_CONTEXT, 'product delete error is a string', {
      length: text.length,
    });
    return text || PRODUCT_DELETE_ERROR_FALLBACK;
  }

  if (Array.isArray(data)) {
    const messages = fieldMessages({ messages: data });
    logInfo(LOG_CONTEXT, 'product delete error is a list', {
      count: messages.length,
    });
    return messages.length
      ? messages.join(' ')
      : PRODUCT_DELETE_ERROR_FALLBACK;
  }

  if (typeof data !== 'object') {
    logInfo(LOG_CONTEXT, 'product delete error is not an object', {
      valueType: typeof data,
    });
    return PRODUCT_DELETE_ERROR_FALLBACK;
  }

  const detail = typeof data.detail === 'string' ? data.detail.trim() : '';

  if (isOpenProcurementDeleteBody(data)) {
    const names = [
      ...quoteNamesMissingFromDetail(data.open_quotes, detail),
      ...orderNamesMissingFromDetail(data.open_orders, detail),
    ];
    logInfo(LOG_CONTEXT, 'formatted open procurement delete refusal', {
      code: data.code ?? null,
      appendedNames: names,
      usedDetail: Boolean(detail),
      openQuotes: Array.isArray(data.open_quotes) ? data.open_quotes.length : 0,
      openOrders: Array.isArray(data.open_orders) ? data.open_orders.length : 0,
    });
    if (!names.length) {
      return detail || PRODUCT_DELETE_ERROR_FALLBACK;
    }
    const clause = `Close ${formatNameList(names)} before deleting this catalog number.`;
    return detail ? `${detail} ${clause}` : clause;
  }

  if (detail) return detail;

  if (typeof data.error === 'string' && data.error.trim()) {
    return data.error.trim();
  }

  const messages = fieldMessages(data);
  logInfo(LOG_CONTEXT, 'formatted product delete field errors', {
    count: messages.length,
  });
  return messages.length ? messages.join(' ') : PRODUCT_DELETE_ERROR_FALLBACK;
}
